import * as Dialog from "@radix-ui/react-dialog";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { Button } from "components/elements";
import { MultiUserSelector } from "components/form/MultiUserSelector";
import { OrganizationSelector } from "components/form/OrganizationSelector";
import { useAllOrganizations } from "hooks/useAllOrganizations";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { useExternalOrgConfirm } from "./useExternalOrgConfirm";

type Participant = {
    id: number;
    name: string;
    avatarUrl: string | null;
};

type ManageParticipantsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    discussionId: number;
    currentParticipants: Participant[];
};

export const ManageParticipantsModal = ({
    isOpen,
    onClose,
    discussionId,
    currentParticipants,
}: ManageParticipantsModalProps) => {
    const currentUser = useCurrentUser();
    const utils = trpc.useUtils();
    const { enqueueSnackbar } = useSnackbar();

    const [pendingRemovals, setPendingRemovals] = useState<Set<number>>(new Set());
    const [usersToAdd, setUsersToAdd] = useState<number[]>([]);
    // PRTL-1348: an org must be selected before people can be added — each
    // addition snapshots the org it was made under (participant = org + user).
    const [selectedOrganization, setSelectedOrganization] = useState<number | null | undefined>();
    const [orgByUserId, setOrgByUserId] = useState<Record<number, number>>({});

    const organizationsQuery = useAllOrganizations(currentUser ?? undefined);
    const organizations = organizationsQuery.data ?? [];

    const usersListQuery = trpc.users.list.useQuery({}, { enabled: isOpen });
    const usersList = usersListQuery.data?.users ?? [];

    // Candidates are scoped to the selected org (the context they'd be added
    // under); the unscoped list above keeps resolving existing participants.
    const candidatesQuery = trpc.users.list.useQuery(
        { organizationId: selectedOrganization ?? undefined },
        { enabled: isOpen && selectedOrganization != null },
    );
    const candidatesList = candidatesQuery.data?.users ?? [];

    const usersById = useMemo(() => new Map(usersList.map((u) => [u.id, u])), [usersList]);

    const currentParticipantIds = useMemo(
        () => new Set(currentParticipants.map((p) => p.id)),
        [currentParticipants],
    );

    // Existing participants' orgs (resolved from the user list) seed the
    // cross-org guard so we only warn when an addition introduces a *new*
    // 2nd+ external org (PRTL-1324).
    const existingParticipantUsers = useMemo(
        () =>
            currentParticipants
                .map((p) => usersById.get(p.id))
                .filter((u): u is (typeof usersList)[number] => Boolean(u)),
        [currentParticipants, usersById],
    );

    const { confirmAdditions, modal: externalOrgConfirmModal } = useExternalOrgConfirm({
        existingUsers: existingParticipantUsers,
    });

    const addCandidates = useMemo(
        () =>
            candidatesList.filter(
                (u) =>
                    u.id !== currentUser?.id &&
                    u.isPrivateEnabled &&
                    !currentParticipantIds.has(u.id),
            ),
        [candidatesList, currentUser?.id, currentParticipantIds],
    );

    // MultiUserSelector resolves its selected chips out of `users`, so anyone
    // picked under a previously-selected org would render as a blank chip once
    // the org filter moves on. Picks deliberately survive an org change (each
    // keeps the org it was made under), so the already-selected users are
    // resolved from the unscoped list and appended — the *selectable*
    // candidates stay scoped to the chosen org.
    const selectorUsers = useMemo(() => {
        const shown = new Map(addCandidates.map((u) => [u.id, u]));
        for (const id of usersToAdd) {
            if (shown.has(id)) continue;
            const user = usersById.get(id);
            if (user) shown.set(id, user);
        }
        return [...shown.values()];
    }, [addCandidates, usersToAdd, usersById]);

    const addMutation = trpc.discussions.addParticipants.useMutation();
    const removeMutation = trpc.discussions.removeParticipants.useMutation();

    const isPending = addMutation.isPending || removeMutation.isPending;

    const reset = () => {
        setPendingRemovals(new Set());
        setUsersToAdd([]);
        setSelectedOrganization(undefined);
        setOrgByUserId({});
    };

    // Snapshot the org each newly-picked user is being added under; keep
    // earlier picks (possibly from another org) with their recorded org.
    const handleUsersToAddChange = (ids: number[]) => {
        if (selectedOrganization != null) {
            setOrgByUserId((prev) => {
                const next: Record<number, number> = {};
                for (const id of ids) {
                    next[id] = prev[id] ?? selectedOrganization;
                }
                return next;
            });
        }
        setUsersToAdd(ids);
    };

    const togglePendingRemoval = (userId: number) => {
        setPendingRemovals((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const handleSave = async () => {
        try {
            if (usersToAdd.length > 0) {
                // Guard on the orgs the additions are bound to (PRTL-1348),
                // falling back to display org for any pick without a snapshot.
                const addOrgBearers = usersToAdd.map((id) => ({
                    organizationId: orgByUserId[id] ?? usersById.get(id)?.organizationId,
                }));
                const confirmed = await confirmAdditions(addOrgBearers);
                if (!confirmed) return;
                const participants = usersToAdd
                    .filter((id) => orgByUserId[id] !== undefined)
                    .map((id) => ({ userId: id, organizationId: orgByUserId[id] }));
                await addMutation.mutateAsync({
                    discussionId,
                    userIds: usersToAdd,
                    // Only send the org-scoped shape when it covers every
                    // pick — otherwise let the server infer (legacy path).
                    participants:
                        participants.length === usersToAdd.length ? participants : undefined,
                });
            }
            if (pendingRemovals.size > 0) {
                await removeMutation.mutateAsync({
                    discussionId,
                    userIds: Array.from(pendingRemovals),
                });
            }
            await utils.discussions.getDiscussionView.invalidate({ discussionId });
            await utils.discussions.list.invalidate();
            reset();
            onClose();
        } catch (err) {
            Sentry.captureException(err, { tags: { feature: "manage-participants" } });
            const message =
                err instanceof Error && err.message ? err.message : "Failed to update recipients.";
            enqueueSnackbar(message, { variant: "error" });
        }
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            reset();
            onClose();
        }
    };

    const hasChanges = usersToAdd.length > 0 || pendingRemovals.size > 0;

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
            <Dialog.Content
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="fixed top-1/2 left-1/2 z-50 max-h-[95vh] w-[95vw] max-w-[500px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-lg"
            >
                <div className="flex items-center justify-between">
                    <div className="font-sans text-[24px] font-bold text-[#424242]">
                        Manage recipients
                    </div>
                    <Dialog.Close>
                        <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                    </Dialog.Close>
                </div>
                <hr className="mx-[-24px] my-4 border-[#c8c8c8]" />

                <div className="flex flex-col gap-4">
                    <div>
                        <div className="mb-2 font-semibold text-[#424242]">
                            Current participants
                        </div>
                        {currentParticipants.length === 0 ? (
                            <div className="text-sm text-gray-500">No active participants.</div>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {currentParticipants.map((p) => {
                                    const willBeRemoved = pendingRemovals.has(p.id);
                                    return (
                                        <li
                                            key={p.id}
                                            className={`flex items-center justify-between rounded border px-3 py-2 ${
                                                willBeRemoved
                                                    ? "border-red-200 bg-red-50"
                                                    : "border-gray-200"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {p.avatarUrl ? (
                                                    <img
                                                        src={p.avatarUrl}
                                                        alt={p.name}
                                                        className="h-7 w-7 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-300 text-xs font-semibold text-white">
                                                        {p.name?.[0]}
                                                    </div>
                                                )}
                                                <span
                                                    className={`text-sm ${
                                                        willBeRemoved
                                                            ? "text-gray-400 line-through"
                                                            : "text-gray-800"
                                                    }`}
                                                >
                                                    {p.name}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => togglePendingRemoval(p.id)}
                                                className="text-sm text-blue-600 hover:underline"
                                            >
                                                {willBeRemoved ? "Undo" : "Remove"}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div>
                        <div className="mb-2 font-semibold text-[#424242]">Add recipients</div>
                        <div className="mb-2">
                            <div className="mb-1 text-sm font-semibold text-[#424242]">
                                <span className="text-red-500">*</span> Organization
                            </div>
                            <OrganizationSelector
                                inputClassName="h-10"
                                withClear
                                value={selectedOrganization}
                                onChange={setSelectedOrganization}
                                organizations={organizations}
                                placeholder="Select an organization"
                            />
                        </div>
                        {selectedOrganization != null ? (
                            <MultiUserSelector
                                value={usersToAdd}
                                users={selectorUsers}
                                onChange={(ids) => handleUsersToAddChange((ids ?? []) as number[])}
                            />
                        ) : (
                            <div className="flex h-10 items-center rounded bg-gray-100 px-3 text-sm text-gray-400">
                                Select an organization first
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleOpenChange(false)}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleSave}
                            disabled={!hasChanges || isPending}
                        >
                            {isPending ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
                {externalOrgConfirmModal}
            </Dialog.Content>
        </Dialog.Root>
    );
};
